import Head from 'next/head';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { getModuleThemeContextId, getModuleThemeCss } from '@/utils/theming-system/modules';
import { Maybe, PbsKidsTheme } from '@/types/pbskids-graph';
import { ModuleBackgroundType } from '@/utils/theming-system/modules';
import { setLookInsidePlayTextColor } from '@/components/modules/LookInsideCard/text-color';
import { ThemeContextName } from '@/utils/theming-system/types';
import GlobalStateContext from '@/components/global/GlobalState';

import styles from './ThemedModule.module.scss';

function BackgroundLayers({ backgroundMode }: { backgroundMode: ModuleBackgroundType }) {
  return <>
    <div className={styles.bgLayers}>
      <div className={styles.solidColor} />

      {backgroundMode === 'pattern' && <div className={styles.pattern} />}
      {backgroundMode === 'image' && <div className={styles.image} />}
    </div>
  </>;
}

type DebuggerProps = {
  css: string,
  theme: PbsKidsTheme
  themeContextId: string
  backgroundMode: ModuleBackgroundType
};

const DebuggerTool = ({ css, theme, backgroundMode, themeContextId }: DebuggerProps) => <>
  <div className={styles.themedModuleDebugger}>
    <details>
      <summary>View Rendered Module Theme CSS</summary>
      <pre dangerouslySetInnerHTML={{ __html: css }}></pre>
    </details>
    <details>
      <summary>View Source Theme Data</summary>
      <pre dangerouslySetInnerHTML={
        {
          __html: JSON.stringify(
            {
              themeContextId,
              backgroundMode,
              theme,
            },
            null, 1,
          ),
        }
      } />
    </details>
  </div>
</>;

type Props = {
  theme?: Maybe<PbsKidsTheme> | Maybe<Array<Maybe<PbsKidsTheme>>>
  backgroundMode: ModuleBackgroundType
  contentClassName?: string
  defaultWrapperClass?: boolean
  moduleType?: string
  themeContextName?: ThemeContextName
  wrapperClassName?: string
  xPadding?: boolean
  children?: ReactNode
};

const allowedThemedModuleContexts: Array<ThemeContextName> = [
  'module',
  'footer',
];

// In some cases we reuse React components for multiple module types, but we only want
// to allow theming for certain modules, which we will add to this list:
const allowedModuleTypes = [
  'BodyContentModulesGamesContentCards',
  'BodyContentModulesVideoContentCards',
  'BodyContentModulesVideoPlaylist',
];

export default function ThemedModule({
  theme,
  backgroundMode,
  contentClassName,
  defaultWrapperClass = true,
  moduleType,
  themeContextName = 'module',
  wrapperClassName,
  xPadding = true,
  children,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement|null>(null);
  const { showThemeDevUI } = useContext(GlobalStateContext);
  const [ showDebugTag, setShowDebugTag ] = useState<boolean>(false);

  theme = Array.isArray(theme) ? theme[0] : theme;

  const useSuppliedTheme = !!theme?.slug && (!moduleType || allowedModuleTypes.includes(moduleType));
  const wrapperClasses = (defaultWrapperClass ? styles.moduleWrapper : '') + (wrapperClassName ? ` ${wrapperClassName}` : '');
  const contentClasses = styles.innerContent + (contentClassName ? ` ${contentClassName}` : '') + (xPadding && useSuppliedTheme ? '' : ` ${styles.noXPadding}`);

  useEffect(() => {
    if (showDebugTag !== showThemeDevUI) {
      setShowDebugTag(showThemeDevUI);
    }

    if (wrapperRef.current) {
      setLookInsidePlayTextColor(
        [ wrapperRef.current as HTMLElement ],
      );
    }
  }, [ showDebugTag, showThemeDevUI ]);

  // If no proper theme is provided, render the children without any theming
  if (!theme?.slug || !useSuppliedTheme) {
    return <>
      <div className={wrapperClasses}>
        <div className={contentClasses}>
          {children}
        </div>
      </div>
    </>;
  }

  const css = getModuleThemeCss(theme, backgroundMode, themeContextName);
  const themeContextId = getModuleThemeContextId(theme, backgroundMode);

  let resolvedBackgroundMode = backgroundMode;
  if (
    (backgroundMode === 'image' && !theme.backgroundImage?.[0]) ||
    (backgroundMode === 'pattern' && !theme.backgroundPattern?.[0])
  ) {
    resolvedBackgroundMode = 'color';
  }

  return <>
    <div
      data-theme-context={themeContextName}
      data-theme-background-mode={resolvedBackgroundMode}
      data-theme-module-contextid={allowedThemedModuleContexts.includes(themeContextName) ? themeContextId : undefined}
      className={wrapperClasses}
      ref={wrapperRef}
    >
      {allowedThemedModuleContexts.includes(themeContextName) && <Head>
        <style
          data-pbsk-theme-styles-source={themeContextName}
          data-pbsk-theme-module-contextid={themeContextId}
          dangerouslySetInnerHTML={{ __html: css }}
        />
      </Head>}

      <BackgroundLayers backgroundMode={resolvedBackgroundMode} />

      <div className={contentClasses}>
        {children}
      </div>

      {(themeContextName === 'module' && showDebugTag) && <DebuggerTool
        css={css}
        backgroundMode={resolvedBackgroundMode}
        themeContextId={themeContextId}
        theme={theme}
      />}
    </div>
  </>;
}
